/**
 * Checks if a value is not null or undefined and narrows the type.
 *
 * @param value - The value to check.
 * @returns `true` if the value is not null or undefined, `false` otherwise.
 * @example const fruits = ["apple", "banana", null, "cherry"];
 * typeof fruits; // (string | null)[]
 * const filteredFruits = fruits.filter(notNull); // ["apple", "banana", "cherry"]
 * typeof filteredFruits; // string[]
 */
export const notNull = <T>(value: T | null | undefined): value is T =>
  value !== null && value !== undefined;

/**
 * Represents a type that can be either `null`, `undefined`, or a specific type `T`.
 * If the type `T` is already nullable, it will be returned as is.
 * Otherwise, it will be wrapped in a union with `null`.
 *
 * @typeParam T - The type to make nullable.
 * @returns The nullable type.
 */
export type Nullable<T> = T | null | undefined;

/**
 * Type guard that checks if a value is non-nullable.
 *
 * @param value - The value to be checked.
 * @returns A boolean indicating if the value is non-nullable.
 */
export type NonNullable = <T>(
  value: T,
) => value is Exclude<T, null | undefined>;
